import { App, Button, Modal, Space, Typography } from 'antd';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { messageFor } from '@/lib/api/index.js';
import { space } from '@/design/tokens.js';
import { useAcknowledgePolicy, useOutstandingPolicies } from './policies.api.js';
import { PolicyReader, type ReadablePolicy } from './PolicyReader.js';

const { Paragraph, Text } = Typography;

/**
 * The acknowledgement prompt (SRS §8.1).
 *
 * **Mandatory policies block; the rest do not.** A prompt that can always be
 * dismissed is not evidence that anybody read anything — and blocking somebody
 * out of their own attendance page over an office-timings note is
 * disproportionate. So the two behave differently, deliberately.
 *
 * Blocking here means a modal with no close button and no mask-click escape:
 * the only ways out are to read the policy and acknowledge it, or to sign out.
 * It never blocks the policies page itself, because that is where the reading
 * happens.
 */
export function PolicyPrompt() {
  const outstanding = useOutstandingPolicies();
  const acknowledge = useAcknowledgePolicy();
  const navigate = useNavigate();
  const location = useLocation();
  const { message } = App.useApp();

  const [dismissed, setDismissed] = useState(false);
  const [reading, setReading] = useState<ReadablePolicy | null>(null);

  const items = outstanding.data?.items ?? [];
  const required = items.filter((item) => item.mandatory);
  const optional = items.filter((item) => !item.mandatory);

  // Never in the way of the page where the reading and acknowledging happen.
  const onPolicies = location.pathname.startsWith('/policies');
  if (onPolicies || items.length === 0) return null;

  const blocking = required.length > 0;
  if (!blocking && dismissed) return null;

  const shown = blocking ? required : optional;

  return (
    <Modal
      open
      title={
        blocking
          ? shown.length === 1
            ? 'One policy needs your acknowledgement'
            : `${shown.length} policies need your acknowledgement`
          : 'New policies to read'
      }
      // A blocking prompt has no way out but the policy itself.
      closable={!blocking}
      maskClosable={!blocking}
      keyboard={!blocking}
      onCancel={() => setDismissed(true)}
      footer={
        <Space>
          {!blocking && <Button onClick={() => setDismissed(true)}>Later</Button>}
          <Button
            type="primary"
            onClick={() => {
              setDismissed(true);
              void navigate('/policies');
            }}
          >
            Open policies
          </Button>
        </Space>
      }
    >
      <Paragraph>
        {blocking
          ? 'These are required reading. Open each one and confirm you have read it.'
          : 'These have been published since you last acknowledged them.'}
      </Paragraph>

      <Space direction="vertical" size="small" style={{ width: '100%', marginBottom: space[3] }}>
        {shown.map((item) => (
          <Space key={item.policyId} align="center" style={{ width: '100%' }} wrap>
            <Text strong>{item.title}</Text>
            {/* Before the confirmation, not after it (orbit-ui#39), and now
                the only button here (orbit-ui#171).

                The prompt used to ask people to confirm they had read
                something it would not show them, and the confirmation sat
                right next to the link — so the shortest way out of a
                blocking prompt was to press it without opening anything.
                Reading comes first, and the only place to say you have read
                it is on the document. */}
            <Button
              size="small"
              type="primary"
              onClick={() =>
                setReading({
                  policyId: item.policyId,
                  versionId: item.versionId,
                  title: item.title,
                })
              }
            >
              Read the policy
            </Button>
          </Space>
        ))}
      </Space>

      <Text type="secondary">Your name, the time and your IP address are recorded.</Text>

      <PolicyReader
        policy={reading}
        onClose={() => setReading(null)}
        acknowledge={
          reading
            ? {
                pending: acknowledge.isPending,
                onAcknowledge: () =>
                  acknowledge.mutate(reading.policyId, {
                    onSuccess: () => {
                      message.success(`Acknowledged ${reading.title}.`);
                      setReading(null);
                    },
                    onError: (error) => message.error(messageFor(error)),
                  }),
              }
            : undefined
        }
      />
    </Modal>
  );
}
